import type { Page } from '@inertiajs/core';
import { navigateBundledInertiaPage } from '@/mobile/native/navigateBundledInertiaPage';
import type { FieldSyncOutboxItem } from '@/mobile/sync/fieldSyncCache';
import {
  loadFieldCache,
  loadOutbox,
  peekFieldCacheMemory,
  updateFieldCacheReports,
  type FieldSyncCache,
} from '@/mobile/sync/fieldSyncCache';
import { normalizeFieldReport } from '@/mobile/sync/normalizeFieldReport';
import type { RdoSubmitPayload } from '@/modules/field/composables/useRdoSubmit';
import type {
  FieldProjectListItem,
  FieldReportListItem,
  FieldReportSituation,
} from '@/modules/field/types';
import type { RdoAtividade } from '@/modules/field/types/rdo-form';
import type { FieldReportDetail } from '@/modules/field/types/report-detail';
import {
  enrichAtividadeForDisplay,
  estimateReportProgressFromPayload,
} from '@/modules/field/utils/rdoActivityDisplay';
import { sortFieldReportsChronologically } from '@/modules/field/utils/fieldReportListSort';
import { resolveSchedulePayload } from '@/modules/field/utils/fieldReportScheduleLabels';

const REPORT_SHOW_COMPONENT = 'modules/field/pages/ReportShow';

export function localReportIdFromClientUuid(clientUuid: string): number {
  let hash = 0;
  for (let i = 0; i < clientUuid.length; i++) {
    hash = (Math.imul(31, hash) + clientUuid.charCodeAt(i)) | 0;
  }

  return -Math.abs(hash || 1);
}

export function isPendingLocalReport(report: FieldReportListItem): boolean {
  return report.sync === 'not_synced' && (report.id < 0 || Boolean(report.clientUuid));
}

function formatReportDate(iso: string): { date: string; reportDateIso: string } {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) {
    return { date: '—', reportDateIso: iso };
  }

  return {
    date: `${d}/${m}/${y}`,
    reportDateIso: iso,
  };
}

export function buildPendingListItemFromSubmit(
  data: RdoSubmitPayload,
  clientUuid: string,
  projects: FieldProjectListItem[],
): FieldReportListItem {
  const project = projects.find((entry) => entry.id === data.projectId);
  const { date, reportDateIso } = formatReportDate(data.reportDate);
  const situation = data.situation as FieldReportSituation;
  const id = data.serverId ?? localReportIdFromClientUuid(clientUuid);
  const code = project?.code ?? '';
  const name = project?.name ?? 'Obra';
  const typeAbbr = (data.type ?? 'rdo').toUpperCase();
  const title = `${typeAbbr} #offline — ${code ? `${code} - ` : ''}${name}`;

  return normalizeFieldReport({
    id,
    clientUuid,
    date,
    reportDateIso,
    projectId: data.projectId,
    clientId: project?.clientId ?? null,
    divisionId: project?.divisionId ?? null,
    type: data.type,
    client: project?.client ?? '—',
    division: project?.division ?? '—',
    reportType: typeAbbr,
    situation,
    deadlineStatus: 'on_time',
    title,
    progress: estimateReportProgressFromPayload(data.payload),
    sync: 'not_synced',
    createdBy: null,
    collaboratorUserIds: [],
    activityScheduleItemIds: [],
    canEdit: id > 0,
    canEvaluate: false,
    canDelete:
      situation === 'draft'
      || situation === 'pending'
      || situation === 'in_approval',
    canSync: true,
  });
}

function buildPendingListItemFromOperation(
  operation: FieldSyncOutboxItem['operation'],
  projects: FieldProjectListItem[],
  outboxItem?: FieldSyncOutboxItem,
): FieldReportListItem {
  const payload = operation.payload;
  const reportDate = String(payload.reportDate ?? '');
  const projectId = Number(payload.projectId ?? 0);
  const situation = String(payload.situation ?? 'pending') as FieldReportSituation;
  const clientUuid = operation.clientUuid;

  const item = buildPendingListItemFromSubmit(
    {
      type: (payload.type as RdoSubmitPayload['type']) ?? 'rdo',
      reportDate,
      projectId,
      situation,
      serverId: operation.serverId,
      payload,
    },
    clientUuid,
    projects,
  );

  if (outboxItem?.lastPushError) {
    return {
      ...item,
      syncError: outboxItem.lastPushError,
    };
  }

  return item;
}

/** Reconstrói cards pendentes a partir da fila (ex.: após reabrir o app). */
export async function syncPendingReportsIntoCache(): Promise<void> {
  const outbox = await loadOutbox();
  const cache =
    peekFieldCacheMemory() ?? (await loadFieldCache({ forceStorage: true }));
  const projects = cache?.projects ?? [];

  if (!cache) {
    return;
  }

  const syncedClientUuids = new Set(
    cache.reports
      .filter((report) => report.sync === 'synced' && report.clientUuid)
      .map((report) => report.clientUuid as string),
  );
  const outboxClientUuids = new Set(
    outbox
      .filter((entry) => entry.operation.entity === 'field_report')
      .map((entry) => entry.operation.clientUuid),
  );

  let reports = cache.reports.filter((report) => {
    if (report.sync !== 'not_synced') {
      return true;
    }

    if (report.clientUuid && !outboxClientUuids.has(report.clientUuid)) {
      return false;
    }

    return true;
  });

  const pendingToUpsert: FieldReportListItem[] = [];

  for (const entry of outbox) {
    if (entry.operation.entity !== 'field_report') {
      continue;
    }

    if (syncedClientUuids.has(entry.operation.clientUuid)) {
      continue;
    }

    const item = buildPendingListItemFromOperation(entry.operation, projects, entry);
    pendingToUpsert.push(item);

    const key = item.clientUuid ?? String(item.id);
    reports = [
      item,
      ...reports.filter((report) => {
        const reportKey = report.clientUuid ?? String(report.id);
        return reportKey !== key && report.id !== item.id;
      }),
    ];
  }

  if (
    pendingToUpsert.length === 0
    && reports.length === cache.reports.length
  ) {
    return;
  }

  await updateFieldCacheReports(reports, pendingToUpsert);
}

async function writeJsonReports(reports: FieldReportListItem[]): Promise<void> {
  await updateFieldCacheReports(reports);
}

function isReportOnServer(
  report: FieldReportListItem,
  serverIds: Set<number>,
  serverClientUuids: Set<string>,
): boolean {
  if (report.id > 0 && serverIds.has(report.id)) {
    return true;
  }

  if (report.clientUuid && serverClientUuids.has(report.clientUuid)) {
    return true;
  }

  return false;
}

export function mergeReportsWithPending(
  server: FieldReportListItem[],
  cache: FieldSyncCache | null,
  includeStaleSyncedFromCache = false,
): FieldReportListItem[] {
  const cached = (cache?.reports ?? []).map((report) => normalizeFieldReport(report));
  const pending = cached.filter((report) => report.sync === 'not_synced');
  const syncedFromCache = cached.filter((report) => report.sync === 'synced');
  const serverNormalized = server.map((item) => normalizeFieldReport(item));
  const serverIds = new Set(serverNormalized.map((report) => report.id));
  const serverClientUuids = new Set(
    serverNormalized
      .map((report) => report.clientUuid)
      .filter((value): value is string => Boolean(value)),
  );

  const locals = pending.filter(
    (report) => !isReportOnServer(report, serverIds, serverClientUuids),
  );

  const cacheOnlySynced = includeStaleSyncedFromCache
    ? syncedFromCache.filter(
        (report) => !isReportOnServer(report, serverIds, serverClientUuids),
      )
    : [];

  const merged = [...locals, ...cacheOnlySynced, ...serverNormalized];

  return sortFieldReportsChronologically(merged);
}

export async function resolvePendingReportPayload(
  clientUuid: string,
): Promise<Record<string, unknown> | null> {
  const outbox = await loadOutbox();

  for (let index = outbox.length - 1; index >= 0; index--) {
    const entry = outbox[index];
    if (entry.operation.clientUuid === clientUuid) {
      return entry.operation.payload;
    }
  }

  return null;
}

export function buildReportDetailFromPending(
  listItem: FieldReportListItem,
  payload: Record<string, unknown>,
): FieldReportDetail {
  const project = {
    id: listItem.projectId,
    code: null as string | null,
    description: listItem.title.split('—').pop()?.trim() ?? '—',
    client: listItem.client,
    division: listItem.division,
  };

  const rawAtividades = Array.isArray(payload.atividades)
    ? (payload.atividades as RdoAtividade[])
    : [];

  return {
    ...listItem,
    number: 'Offline',
    project,
    creator: null,
    updater: null,
    syncedAt: null,
    createdAt: new Date().toISOString(),
    reportDate: listItem.reportDateIso,
    basic: {
      serviceOnTime: (payload.serviceOnTime as boolean | null) ?? null,
      startTime: (payload.startTime as string | null) ?? null,
      endTime: (payload.endTime as string | null) ?? null,
      hasBreak: Boolean(payload.hasBreak),
      breakStart: (payload.breakStart as string | null) ?? null,
      breakEnd: (payload.breakEnd as string | null) ?? null,
      breakMinutes: (payload.breakMinutes as number | null) ?? null,
      totalHours: (payload.totalHours as number | string | null) ?? null,
      weatherMorning: (payload.weatherMorning as string | null) ?? null,
      weatherAfternoon: (payload.weatherAfternoon as string | null) ?? null,
    },
    colaboradores: Array.isArray(payload.colaboradores)
      ? payload.colaboradores
      : [],
    intercorrencias: Array.isArray(payload.intercorrencias)
      ? payload.intercorrencias
      : [],
    pendencias: Array.isArray(payload.pendencias) ? payload.pendencias : [],
    atividades: rawAtividades.map((row) => enrichAtividadeForDisplay(row)),
    evidencias: Array.isArray(payload.evidencias) ? payload.evidencias : [],
    payload: resolveSchedulePayload(payload),
  };
}

export async function visitLocalFieldReportShow(
  report: FieldReportListItem,
): Promise<void> {
  const clientUuid = report.clientUuid;
  if (!clientUuid) {
    throw new Error('Relatório offline sem identificador local.');
  }

  const payload = await resolvePendingReportPayload(clientUuid);
  if (!payload) {
    throw new Error('Dados do RDO offline não encontrados na fila local.');
  }

  const detail = buildReportDetailFromPending(report, payload);
  const path = `/field/reports/${report.id}`;

  const page: Page = {
    component: REPORT_SHOW_COMPONENT,
    props: { report: detail },
    url: path,
    version: '',
    clearHistory: false,
    encryptHistory: false,
  };

  await navigateBundledInertiaPage(page);
}
